import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, { 
  Background, Controls, MiniMap, useNodesState, useEdgesState, MarkerType,
  ReactFlowProvider, useReactFlow
} from "reactflow";
import "reactflow/dist/style.css";
import PageLoader from "./PageLoader";
import { fetchChildren, buildHierarchy, computeDepths, fetchMemberById } from "../script/rpc";
import MlmNode from "./MlmNode";
import InfoDrawer from "./InfoDrawer";

const nodeTypes = { mlmNode: MlmNode };
const NODE_W = 220;
const NODE_H = 200;

function computeWidth(node) {
  if (!node.children || node.children.length === 0) { node._w = 1; return; }
  node.children.forEach(computeWidth);
  node._w = node.children.reduce((s, c) => s + c._w, 0);
}

function placeNodes(node, depth, left, rfNodes, rfEdges) {
  const cx = (left + node._w / 2) * NODE_W;
  const y = depth * NODE_H;
  rfNodes.push({
    id: String(node.id),
    type: "mlmNode",
    position: { x: cx - 100, y },
    data: { member: node, depth, isSelected: false, expanded: true, onSelect: () => {}, onExpand: () => {} },
  });
  if (node.parent_id && node.parent_id[0]) {
    rfEdges.push({
      id: `e-${node.parent_id[0]}-${node.id}`,
      source: String(node.parent_id[0]),
      target: String(node.id),
      type: "smoothstep",
      style: { stroke: "#7c3aed", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#7c3aed", width: 14, height: 14 },
    });
  }
  let childLeft = left;
  (node.children || []).forEach(child => {
    placeNodes(child, depth + 1, childLeft, rfNodes, rfEdges);
    childLeft += child._w;
  });
}

function buildLayout(roots) {
  const rfNodes = [], rfEdges = [];
  roots.forEach(computeWidth);
  let left = 0;
  roots.forEach(root => {
    placeNodes(root, 0, left, rfNodes, rfEdges);
    left += root._w;
  });
  return { rfNodes, rfEdges };
}

function MemberSubtree() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const auth = JSON.parse(sessionStorage.getItem("mlm_auth") || "null");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rootMember, setRootMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Slowly zoom based on wheel delta (smoothly without lagging)
  const { getViewport, setViewport } = useReactFlow();
  const handleWheel = useCallback((e) => {
    const { x, y, zoom } = getViewport();
    const delta = -e.deltaY * 0.0008;
    const newZoom = Math.min(2, Math.max(0.01, zoom + delta));
    setViewport({ x, y, zoom: newZoom });
  }, [getViewport, setViewport]);

  useEffect(() => {
    if (!auth) { navigate("/login"); return; }
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [rootRec, children] = await Promise.all([
          fetchMemberById(auth.uid, auth.password, Number(memberId)),
          fetchChildren(auth.uid, auth.password, Number(memberId), true)
        ]);
        if (!alive) return;
        setRootMember(rootRec);
        const all = [rootRec, ...children];
        const roots = computeDepths(buildHierarchy(all));
        const { rfNodes, rfEdges } = buildLayout(roots);
        setNodes(rfNodes);
        setEdges(rfEdges);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [auth, memberId, setNodes, setEdges, navigate]);

  return (
    <div 
      onContextMenu={(e) => e.preventDefault()}
      style={{ 
        position: "fixed", inset: 0, zIndex: 1000,
        display: "flex", flexDirection: "column", background: "#f8fafc", fontFamily: "'Inter', sans-serif" 
      }}
    >
      {loading && <PageLoader message="Fetching full downline…" />}

      {!loading && (
        <header style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "0 20px", height: 58, flexShrink: 0,
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        }}>
          <button onClick={() => navigate(-1)} style={{
            padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer"
          }}>← Back</button>

          <button onClick={() => window.close()} style={{
            padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer"
          }}>Close Tab</button>

          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
              {rootMember ? `${rootMember.name}'s Network` : "Subtree View"}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>Full Downline Visualization</div>
          </div>
        </header>
      )}

      {!loading && (
        <div style={{ flex: 1, position: "relative" }}>
          {error && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 24 }}>
              <div style={{ fontSize: 40 }}>⚠️</div>
              <h3 style={{ color: "#dc2626", fontSize: 16 }}>Error loading branch</h3>
              <p style={{ color: "#64748b", fontSize: 13 }}>{error}</p>
            </div>
          )}
          {!error && (
            <ReactFlow
              nodes={nodes} edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView fitViewOptions={{ padding: 0.15 }}
              minZoom={0.01} maxZoom={2}
              translateExtent={[[ -20000, -20000], [20000, 20000]]}
              nodesDraggable nodesConnectable={false}
              panOnScroll={false} zoomOnScroll={false} zoomOnPinch={true}
              onWheel={handleWheel}
              onNodeClick={(_, node) => setSelectedMember(node.data.member)}
            >
              <Background color="#cbd5e1" gap={20} size={1} style={{ background: "#f1f5f9" }} />
              <Controls showInteractive={false} />
              <MiniMap />
            </ReactFlow>
          )}
        </div>
      )}

      <InfoDrawer member={selectedMember} onClose={() => setSelectedMember(null)} />
    </div>
  );
}

export default function MemberSubtreeWrapper() {
  return (
    <ReactFlowProvider>
      <MemberSubtree />
    </ReactFlowProvider>
  );
}
